import { BackfillWarningDto } from 'providers/api';
import * as GlobalConstants from '../GConstants';
import {
  NO_WARNING,
  OVER_PRESSURE_POSSIBLE_WARNING,
  OVER_PRESSURE_WARNING,
  RangeBase,
  RoutePoint,
  RouteWarning,
  SLACK_FLOW_POSSIBLE_WARNING,
  SLACK_FLOW_WARNING
} from '../types';
import PCNode from '../Nodes/PCNode';
import { isBoosterNode, isChokeNode, isSyntheticNodeId } from '../Nodes';
import { headMetresToKpa } from '../hydraulics';
import PCPipe from '../Pipes/PCPipe';
import PCSpool from '../Spools/PCSpool';
import { isSyntheticSpool } from '../Spools/utils';
import PCStope from '../Stopes/PCStope';

const OVER_PRESSURE_MIN = 1;
const OVER_PRESSURE_POSSIBLE_MIN = 0.8;
const NO_WARNINGS_MIN = 0.1;
const SLACK_FLOW_POSSIBLE_MIN = 0.01;

const OVER_PRESSURE_POSSIBLE: RangeBase = { min: OVER_PRESSURE_POSSIBLE_MIN, max: OVER_PRESSURE_MIN };
const SLACK_FLOW_POSSIBLE: RangeBase = { min: SLACK_FLOW_POSSIBLE_MIN, max: NO_WARNINGS_MIN };

const SLACK_FLOW_STOPE_EXCLUSION_DISTANCE = 75;

const oneIsInsideRange = (values: number[], range: RangeBase) => values.some((value) => value >= range.min && value < range.max);

export default class PCRoute {
  originNode: PCNode;

  spools: PCSpool[];

  pipeList: Record<string, PCPipe> = {};

  routePoints: RoutePoint[];

  settlingVelocity: number | null = null;

  minimumVelocity: number | null = null;

  selected = false;

  xScale = 1;

  yScale = 1;

  hover = null;

  hasSettlingWarning = false;

  active = false;

  pumpPressureKpa: number | undefined;

  stope: PCStope;

  warnings: BackfillWarningDto[] = [];

  routeLength: number;

  routePointStrings: string[];

  routeSegmentColours: string[] = [];

  constructor(
    originNode: PCNode,
    stope: PCStope,
    spools: PCSpool[],
    routeLength: number,
    routePointStrings: string[],
  ) {
    this.originNode = originNode;
    this.spools = [];
    spools.forEach((sd) => {
      this.spools.push(sd);
    });
    this.routeLength = routeLength;
    this.routePointStrings = routePointStrings;
    this.routePoints = this.setupRoutePoints();
    this.stope = stope;
    this.setupPipeList();
    this.updateRoutePointData();
  }

  setupPipeList() {
    this.spools.forEach((spool) => {
        const { pipe } = spool;
        if (typeof pipe !== 'undefined') this.pipeList[pipe.pipeId] = pipe;
    });
  }

  setupRoutePoints() {
    const nodeLookup: Map<string, PCNode> = new Map();
    this.spools.forEach((spool) => {
      if (!spool.pointA || !spool.pointB) {
        throw new Error('spool does not contain point objects');
      }
      nodeLookup.set(spool.pointA.id, spool.pointA);
      nodeLookup.set(spool.pointB.id, spool.pointB);
    });

    return this.routePointStrings.map((nodeId) => {
      const node = nodeLookup.get(nodeId);
      if (!node) {
        throw new Error(`Node ${nodeId} not found in spools`);
      }
      return { point: node };
    });
  }

  getNodeList(): string[] {
    const nodesSet = new Set<string>();
    this.spools.forEach((spool) => {
      if (spool.pointA && spool.pointB) {
      nodesSet.add(spool.pointA.toString());
      nodesSet.add(spool.pointB.toString());
      }
    });
    return Array.from(nodesSet);
  }

  updateRoutePointPressure(density: number) {
    if (!density || density <= 0 || !Number.isFinite(density)) return;

    let pressure = 0;
    for (let i = 0; i < this.routePoints.length; i += 1) {
      if (i > 0) {
        const fromNodeId = this.routePointStrings[i - 1];
        const toNodeId = this.routePointStrings[i];
        const previousSpool = this.spools[i - 1];

        if (previousSpool) {
          const flowForward = previousSpool.pointB.id === fromNodeId && previousSpool.pointA.id === toNodeId;
          const pressureDelta = previousSpool.getPressureDelta(density, flowForward);
          if (Number.isFinite(pressureDelta)) {
            pressure += pressureDelta;
          }
        }

        const currentNode = this.routePoints[i].point;
        if (currentNode && isBoosterNode(currentNode.id)) {
          pressure = 0;
        } else if (currentNode && isChokeNode(currentNode.id)) {
          // The walk runs stope -> plant, so pressure accumulates upstream: a choke station means the plant
          // side must carry an extra head to be dissipated across it. Always dissipate the full head.
          pressure = Math.max(pressure, 0) + headMetresToKpa(currentNode.chokeHead, density);
        } else {
          pressure = Math.max(pressure, 0);
        }
      }
      this.routePoints[i].pressure = Number.isFinite(pressure) ? pressure : 0;
    }
    this.pumpPressureKpa = Number.isFinite(pressure) ? pressure : 0;
  }

  updateRoutePointData() {
    let distanceFromPump = this.routeLength;
    for (let i = 0; i < this.routePoints.length; i += 1) {
      const previousSpool = this.spools[i - 1];
      const currentSpool = this.spools[i];
      const pipeDownstream = (i > 0) ? previousSpool.pipe : currentSpool.pipe; // No downstream pipe on discharge node
      const pipeUpstream = (i < this.spools.length) ? currentSpool.pipe : undefined; // No upstream pipe on surface node
      if (i > 0) {
        distanceFromPump -= previousSpool.length();
      }

      this.routePoints[i].distanceFromPump = distanceFromPump;
      this.routePoints[i].pipeDownstream = pipeDownstream;
      this.routePoints[i].pipeUpstream = pipeUpstream;
      this.routePoints[i].pressure = 0;
    }
  }

  findNearestNodeIdFromDistanceFromPump(distance: number) {
    const points = this.routePoints;
    let minDist = this.routeLength;
    let nearestPointId = '';
    Object.values(points).forEach((p) => {
      if (p.distanceFromPump) {
        const dist = Math.abs(p.distanceFromPump - distance);
        if (Math.abs(p.distanceFromPump - distance) < minDist) {
          minDist = dist;
          nearestPointId = p.point.id;
        }
      }
    });
    return nearestPointId;
  }

  // TODO: Complete this method
  findMaxPipePressure() {
    let pressureArray = this.routePoints.map((rPoint) => rPoint.pressure ?? 0);
    let maxP = null;
    // remove undefined pressures
    pressureArray = pressureArray.filter((p) => p !== undefined);
    maxP = Math.max(...pressureArray);
    return maxP;
  }

  returnSpoolIdList() {
    const spoolIdList = Object.values(this.spools).map((spool) => spool.spoolId);
    return spoolIdList;
  }

  // The persisted spools this route crosses, in route order (spools are already segment-ordered by
  // orderSpoolsByRoute). Discharge spools and booster/choke twins are presentation only and are left
  // out: they do not exist on the server, so throughput cannot accrue on them.
  get spoolIds(): string[] {
    return this.spools.filter((spool) => !isSyntheticSpool(spool)).map((spool) => spool.spoolId);
  }

  colourRouteByPressure() {
    this.warnings = [];
    const routePts = this.routePoints;
    const routeLen = routePts.length;
    const segmentColours: string[] = [];

    for (let i = 0; i < routeLen; i += 1) {
        const currentPoint = routePts[i];
        const previousPoint = routePts[i + 1];

        if (!currentPoint.pipeUpstream) {
          segmentColours.push(NO_WARNING.color);
          break;
        }

        const { pipeUpstream: { pressureRating } } = currentPoint;
        const pressureA = currentPoint.pressure;
        const pressureB = previousPoint?.pressure;

        if (pressureA === undefined || pressureB === undefined) {
          segmentColours.push(NO_WARNING.color);
          break;
        }

        const percentA = pressureA / pressureRating;
        const percentB = pressureB / pressureRating;

        // Zero-length booster/choke segments are pressure discontinuities by design; suppress slack-flow warnings there.
        const touchesPressureDiscontinuity = isSyntheticNodeId(currentPoint.point.id)
          || (previousPoint?.point ? isSyntheticNodeId(previousPoint.point.id) : false);

        let segmentColour = NO_WARNING.color;

        if (percentA >= OVER_PRESSURE_MIN || percentB >= OVER_PRESSURE_MIN) {
            segmentColour = OVER_PRESSURE_WARNING.color;
            this.addWarning(OVER_PRESSURE_WARNING);
        } else if (!touchesPressureDiscontinuity && (percentA < SLACK_FLOW_POSSIBLE_MIN || percentB < SLACK_FLOW_POSSIBLE_MIN)) {
            segmentColour = SLACK_FLOW_WARNING.color;
            const distanceFromStope = this.routeLength - (currentPoint.distanceFromPump ?? 0);
            if (distanceFromStope > SLACK_FLOW_STOPE_EXCLUSION_DISTANCE) {
                this.addWarning(SLACK_FLOW_WARNING);
            }
        } else if (oneIsInsideRange([percentA, percentB], OVER_PRESSURE_POSSIBLE)) {
            segmentColour = OVER_PRESSURE_POSSIBLE_WARNING.color;
            this.addWarning(OVER_PRESSURE_POSSIBLE_WARNING);
        } else if (!touchesPressureDiscontinuity && oneIsInsideRange([percentA, percentB], SLACK_FLOW_POSSIBLE)) {
            segmentColour = SLACK_FLOW_POSSIBLE_WARNING.color;
            const distanceFromStope = this.routeLength - (currentPoint.distanceFromPump ?? 0);
            if (distanceFromStope > SLACK_FLOW_STOPE_EXCLUSION_DISTANCE) {
                this.addWarning(SLACK_FLOW_POSSIBLE_WARNING);
            }
        } else {
            segmentColour = NO_WARNING.color;
        }

        segmentColours.push(segmentColour);
    }

    this.routeSegmentColours = segmentColours;
  }

  addWarning(newWarning: RouteWarning) {
    if (!this.warnings.some((warning) => warning.code === newWarning.code)) this.warnings = [...this.warnings, newWarning];
  }

  // buzz (Alcwyn): return errors as string array
  findWarnings(): BackfillWarningDto[] {
    return this.warnings;
  }

  // BUZZ: Maybe replaced by the stope drawer
  renderWarnings(ctx: CanvasRenderingContext2D) {
    ctx.save();
    let y = 520;
    const x = 80;
    ctx.font = '12px Arial';
    if (this.warnings.length > 0) {
      ctx.globalAlpha = 0.6;
      ctx.lineWidth = 1;

      ctx.fillStyle = GlobalConstants.GREY;
      ctx.fillText('FAULTS', x + 1, y - 5);

      if (this.warnings.some((warning) => warning.code === 'SF')) {
        ctx.fillStyle = GlobalConstants.PURPLE;
        ctx.fillRect(x, y, 82, 15);
        ctx.fillStyle = GlobalConstants.BLACK;
        ctx.fillText('SLACK FLOW', x + 2, y + 12);
        y += 18;
      }
      if (this.warnings.some((warning) => warning.code === 'OP')) {
        ctx.fillStyle = GlobalConstants.RED;
        ctx.fillRect(x, y, 107, 15);
        ctx.fillStyle = GlobalConstants.BLACK;
        ctx.fillText('OVER PRESSURE', x + 2, y + 12);
      }
    }
    ctx.restore();
  }

  setSettlingVelocity(v: number) {
    this.settlingVelocity = v;
  }

  setMinimumVelocity(v: number) {
    this.minimumVelocity = v;
  }

  changeSegmentColours(colours: string[]) {
    this.routeSegmentColours = colours;
  }
}
